import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { VSidebarItemProps } from "@verbleif/lib/src/components/Sidebar/VSidebarItem.type";
import type { PinnedFilter } from "@verbleif/lib/src/composables/Api/Type/Pin/pin.interface";
import type { PropertyHydraItem, PropertyTaskStateHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyReservationStatesHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyReservationMultiState/propertyReservationState.interface";
import type { ReportTopicHydraItem } from "@verbleif/lib/src/composables/Api/Type/ReportTopic/report_topic.interface";
import type { SortHydraItem } from "@verbleif/lib/src/composables/Api/Type/Sort/sort.interface";
import type { LocationQueryValue } from "vue-router";
import { iconFromStoredName } from "@verbleif/lib/src/components/Icon/storedIconNames";
import { DefaultViewReference } from "@verbleif/lib/src/composables/Views/DefaultViewReference";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { shortcutVoter } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { useReportViews } from "@verbleif/shared/src/composables/ReportViews/useReportViews";
import { effectiveDefaultView } from "@verbleif/shared/src/composables/ScopedViews/defaultView.helpers";
import {
  REPORT_PER_OBJECT_DEFAULT_VIEW,
  REPORT_PER_OBJECT_LABEL_KEY,
  REPORT_VIEW_DEFAULT_ICON,
  REPORT_VIEW_DEFAULT_ICON_COLOR_CLASS,
  REPORT_VIEW_SIDEBAR_LABEL_KEYS,
} from "@verbleif/shared/src/composables/ScopedViews/reportViewSidebarLabels";
import { getColorClass, resolveSidebarIconColor } from "@verbleif/shared/src/composables/ScopedViews/resolveViewIconColor";
import { resolveViewSidebarLabel } from "@verbleif/shared/src/composables/ScopedViews/resolveViewSidebarLabel";
import { useSystemStore } from "@verbleif/shared/src/composables/SystemStore/useSystemStore";
import { BUILTIN_REPORT_VIEW_KEYS, isBuiltinReportViewKey, ReportListType } from "@web/features/Reports/ReportViewKeys";
import { BUILTIN_REPORT_VIEW_DEFINITIONS } from "@web/features/Reports/Views/builtinReportViewDefinitions";
import {
  isReportPropertyBrowseQuery,
  parseReportViewScopeFromQuery,
  REPORT_VIEW_SCOPE_QUERY,
  REPORT_VIEW_SCOPE_QUERY_BY_TYPE,
} from "@web/features/Reports/Views/reportViewScopeQuery";
import { usePropertyReservationStates } from "@web/features/Tasks/TaskExtraData/usePropertyReservationStates";
import { scopeFromViewQuery } from "@web/features/Views/defaultViewQuery";
import { sort } from "fast-sort";
import { computed, inject, provide } from "vue";
import { useRoute } from "vue-router";

const ReportListKey = Symbol("ReportListKey");

// function mapToIri(item: string | HydraItem<any>): string {
//   return typeof item === "string" ? item : item["@id"];
// }

function getPropertyIconInfo(item: {
  reservationState: PropertyReservationStatesHydraItem | null
  taskState: PropertyTaskStateHydraItem | null
}) {
  const reservationState = item.reservationState;

  let tooltipKey: string | null = null;

  // Map color values to tooltip keys
  if (reservationState?.occupationTransitionColorAtDatePoint) {
    switch (reservationState.occupationTransitionColorAtDatePoint) {
      case "blue-600":
        tooltipKey = "tasks.section.tooltip.occupied";
        break;
      case "green-600":
        tooltipKey = "tasks.section.tooltip.hasDeparted";
        break;
      case "red-600":
        tooltipKey = "tasks.section.tooltip.notDeparted";
        break;
    }
  }

  return { tooltipKey };
}

export function createReportList() {
  const route = useRoute();
  const systemStore = useSystemStore();
  const { userPreferences } = useAuthStore();
  const { t, te } = useI18n();
  const { views, enabledViews, byKey, viewManagement, switcherEnabled } = useReportViews();

  const { getSidebarReservationState, hasSidebarReservationState, getSidebarTaskState } = usePropertyReservationStates();

  const filteredTopics = computed<ReportTopicHydraItem[]>(() => {
    return systemStore.reportTopics.value;
  });

  const filteredSorts = computed<SortHydraItem[]>(() => {
    return systemStore.sorts.value;
  });

  function firstQueryValue(value: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
    const single = Array.isArray(value) ? value[0] : value;
    return typeof single === "string" && single !== "" ? single : null;
  }
  const currentViewKey = computed(() => firstQueryValue(route.query.view));
  const scope = computed(() => parseReportViewScopeFromQuery(route.query as Record<string, unknown>));
  /** The per-object list without an object chosen yet. */
  const isPropertyBrowseMode = computed(() => scope.value.property == null
    && isReportPropertyBrowseQuery(route.query as Record<string, unknown>));
  const currentView = computed(() => {
    const key = currentViewKey.value;
    if (!key) {
      return null;
    }
    return byKey.value.get(key) ?? null;
  });
  const currentViewDefinition = computed(() => currentView.value?.definition ?? null);
  /** The `pinned` filter of the current view, with the same built-in fallback as the list parameters. */
  const currentPinnedFilter = computed<PinnedFilter>(() => {
    const key = currentViewKey.value;
    const definition = currentViewDefinition.value ?? (key ? BUILTIN_REPORT_VIEW_DEFINITIONS[key] : null);
    return definition?.filters.pinned ?? "any";
  });
  const isCustomView = computed(() => {
    const key = currentViewKey.value;
    return key != null && !isBuiltinReportViewKey(key);
  });

  const currentListType = computed<ReportListType | null>(() => {
    if (scope.value.property || isPropertyBrowseMode.value) {
      return ReportListType.TYPE_PER_PROPERTY;
    }
    if (scope.value.topic) {
      return ReportListType.TYPE_REPORT_TOPIC;
    }
    if (scope.value.sort) {
      return ReportListType.TYPE_REPORT_SORT;
    }
    const view = currentViewKey.value;
    if (!view) {
      return null;
    }
    if (view === BUILTIN_REPORT_VIEW_KEYS.CREATED_BY_ME) {
      return ReportListType.TYPE_CREATED_BY_ME;
    }
    return view as ReportListType;
  });
  const currentListId = computed<string | null>(() => {
    if (scope.value.property) {
      return scope.value.property;
    }
    if (scope.value.topic) {
      return scope.value.topic;
    }
    if (scope.value.sort) {
      return scope.value.sort;
    }
    return firstQueryValue(route.query.id);
  });

  const listQueryReady = computed(() => {
    const key = currentViewKey.value;
    // The per-object list waits for its object, so it does not list every report meanwhile.
    if (!key || isPropertyBrowseMode.value) {
      return false;
    }
    const effective = byKey.value.get(key);
    const required = effective?.definition.requiredScope ?? null;
    if (!required) {
      return true;
    }
    if (required === "property") {
      return scope.value.property != null;
    }
    if (required === "reportList") {
      return scope.value.topic != null;
    }
    if (required === "sort") {
      return scope.value.sort != null;
    }
    return true;
  });

  /** Views that need a scope (topic, sort, property) are linked from their own sidebar sections. */
  const effectiveViewSidebarItems = computed<VSidebarItemProps[]>(() => {
    return enabledViews.value
      .filter(view => view.definition.requiredScope == null)
      .map(view => ({
        routeId: view.key,
        text: resolveViewSidebarLabel(view, REPORT_VIEW_SIDEBAR_LABEL_KEYS, t, te),
        icon: iconFromStoredName(view.avatarIconIdentifier, REPORT_VIEW_DEFAULT_ICON),
        ...resolveSidebarIconColor(view.avatarColor, REPORT_VIEW_DEFAULT_ICON_COLOR_CLASS),
        to: { name: "report-list", query: { view: view.key } },
        children: [],
      }));
  });

  const currentListRouteId = computed(() => {
    return `${currentListType.value}_${currentListId.value}`;
  });

  const currentList = computed<PropertyHydraItem | ReportTopicHydraItem | SortHydraItem | null>(() => {
    switch (currentListType.value) {
      case ReportListType.TYPE_ALL:
      case ReportListType.TYPE_OPEN:
      case ReportListType.TYPE_CLOSED:
      case ReportListType.TYPE_CREATED_BY_ME:
      case ReportListType.TYPE_GUEST:
      case ReportListType.TYPE_TRANSFERRED:
      case ReportListType.TYPE_PER_PROPERTY:
        return systemStore.properties.value.find((property) => {
          return `${ReportListType.TYPE_PER_PROPERTY}_${property.id}` === currentListRouteId.value;
        }) || null;
      case ReportListType.TYPE_REPORT_TOPIC:
        return filteredTopics.value.find((topic: ReportTopicHydraItem) => {
          return `${ReportListType.TYPE_REPORT_TOPIC}_${topic.id}` === currentListRouteId.value;
        }) || null;
      case ReportListType.TYPE_REPORT_SORT:
        return filteredSorts.value.find((sort: SortHydraItem) => {
          return `${ReportListType.TYPE_REPORT_SORT}_${sort.id}` === currentListRouteId.value;
        }) || null;
      default:
        return null;
    }
  });

  const loading = computed(() => {
    return !!currentList.value;
  });

  const currentListName = computed(() => {
    if (currentList.value) {
      if (currentList.value["@type"] === "Property") {
        return currentList.value[userPreferences.value?.defaultPropertyName || "name"] || "";
      }

      return currentList.value.name;
    }

    if (isPropertyBrowseMode.value) {
      return t(REPORT_PER_OBJECT_LABEL_KEY);
    }

    if (isCustomView.value) {
      return currentView.value?.name ?? currentViewKey.value ?? "";
    }

    return "";
  });

  /**
   * The per-object list is the default when the effective preference names it (no view carries `default` then);
   * otherwise the open list is the default when it is the same view with the same scope as the API resolved.
   */
  const perObjectIsDefault = computed(() => userPreferences.value?.defaultReportView === REPORT_PER_OBJECT_DEFAULT_VIEW);
  const isCurrentDefault = computed(() => {
    if (perObjectIsDefault.value) {
      return currentListType.value === ReportListType.TYPE_PER_PROPERTY;
    }
    const key = currentViewKey.value;
    const reference = effectiveDefaultView(views.value);
    if (!key || !reference || isPropertyBrowseMode.value || !DefaultViewReference.matchesFormat(key)) {
      return false;
    }
    const currentScope = scopeFromViewQuery(route.query as Record<string, unknown>, REPORT_VIEW_SCOPE_QUERY_BY_TYPE);
    return reference.equals(DefaultViewReference.create(key, currentScope));
  });

  /** Scoped lists (topic / sort / property) always run on `view=all`, so scope wins over the view header. */
  const currentListTranslation = computed(() => {
    if (scope.value.topic) {
      return "reports.type.topic";
    }
    if (scope.value.sort) {
      return "reports.type.sort";
    }
    if (scope.value.property) {
      return "reports.type.per_property";
    }
    if (isPropertyBrowseMode.value) {
      return "reports.type.custom";
    }
    const translationKey = currentView.value?.translationKey;
    if (translationKey && te(translationKey)) {
      return translationKey;
    }
    return "reports.type.custom";
  });

  const propertyTabs = computed<VSidebarItemProps[]>(() => {
    const rawProperties = systemStore.properties.value.filter(property => property.active);
    const propertiesWithGroupAndSortPriority = sort(rawProperties.map((property) => {
      const propertyGroup = systemStore.propertyGroupsIndexedByIri.value[property.group];
      const reservationState = getSidebarReservationState(property.id);

      let sortPriority = 7; // Default to lowest priority

      // Update to use new API structure
      const isSwitch = reservationState?.switch && reservationState?.switchAtDatePoint;
      const isDeparture = reservationState?.departure && reservationState?.departureAtDatePoint;
      const isArrival = reservationState?.arrival && reservationState?.arrivalAtDatePoint;
      const expectedOccupation = reservationState?.expectedOccupation ?? false;
      const actualOccupation = reservationState?.actualOccupation ?? false;

      if (isSwitch) {
        sortPriority = 1;
      } else if (isDeparture) {
        sortPriority = 2;
      } else if (isArrival) {
        sortPriority = 3;
      } else if (actualOccupation || expectedOccupation) { // Currently occupied or expected to be occupied
        sortPriority = 5;
      } else { // Vacant
        sortPriority = 6;
      }

      return {
        ...property,
        groupName: propertyGroup?.[userPreferences.value?.defaultPropertyGroupName || "name"] ?? null,
        groupId: propertyGroup?.id ?? null,
        name: property[userPreferences.value?.defaultPropertyName || "name"] ?? "",
        sortPriority, // Add the calculated priority
      };
    })).asc([
      // First sort by group name
      property => property.groupName ?? "",
      // Then by the calculated sort priority
      property => property.sortPriority,
      // Finally by property name as a tie-breaker
      property => property.name,
    ]);

    const items: VSidebarItemProps[] = [];

    // First collect all groups and their properties
    const groupedProperties = propertiesWithGroupAndSortPriority.reduce((acc, property) => {
      // Skip properties without a group
      if (!property.groupName) {
        return acc;
      }

      if (!acc[property.groupName]) {
        acc[property.groupName] = {
          properties: [],
          groupId: property.groupId,
        };
      }
      acc[property.groupName].properties.push(property);
      return acc;
    }, {} as Record<string, { properties: typeof propertiesWithGroupAndSortPriority, groupId: number | null }>);

    // Add groups and their properties
    Object.entries(groupedProperties).forEach(([groupName, group]) => {
      // Create group item with properties as children
      const groupItem: VSidebarItemProps = {
        routeId: `${ReportListType.TYPE_PER_PROPERTY}_group_${group.groupId}`,
        text: groupName,
        icon: "layout-dashboard",
        iconColor: "text-gray-500",
        // hideSpacer: true,
        children: group.properties.map((property) => {
          // Get property state only once and cache it
          const propertyState = hasSidebarReservationState(property.id);

          if (!propertyState) {
            const defaultIconInfo = getPropertyIconInfo({
              reservationState: null,
              taskState: null,
            });

            return {
              routeId: `${ReportListType.TYPE_PER_PROPERTY}_${property.id}`,
              text: property.name,
              icon: "house",
              to: {
                name: "report-list",
                query: {
                  view: BUILTIN_REPORT_VIEW_KEYS.ALL,
                  [REPORT_VIEW_SCOPE_QUERY.property]: String(property.id),
                },
              },
              children: [], // Required by VSidebarItemProps but empty
              iconColor: "text-gray-800 dark:text-gray-100",
              iconTooltip: defaultIconInfo.tooltipKey ? t(defaultIconInfo.tooltipKey) : "",
            };
          }

          // Get the current reservation state for this property
          const reservationState = getSidebarReservationState(property.id);

          // Determine status flags using new API structure
          const isSwitching = reservationState?.switch && reservationState?.switchAtDatePoint;
          const isDeparting = reservationState?.departure && reservationState?.departureAtDatePoint;
          const isArriving = reservationState?.arrival && reservationState?.arrivalAtDatePoint;
          const isBlocked = reservationState?.blocked;

          const iconInfo = getPropertyIconInfo({
            reservationState,
            taskState: getSidebarTaskState(property.id),
          });

          const tooltipText = iconInfo.tooltipKey ? t(iconInfo.tooltipKey) : "";

          let rightIcons: Array<{
            icon: IconName
            tooltip: string
            color: string
            tooltipColor?: string
            showInTooltip: boolean
          }> = [];

          if (isSwitching || isDeparting || isArriving || isBlocked) {
            rightIcons = [];

            if (isSwitching) {
              rightIcons.push({
                icon: "arrow-right-left",
                tooltip: t("tasks.section.object_status.switching"),
                color: getColorClass(reservationState?.transitionIconColorAtDatePoint, "text-blue-500 dark:text-blue-400"),
                showInTooltip: true,
              });
            }

            if (isDeparting) {
              rightIcons.push({
                icon: "log-out",
                tooltip: t("tasks.section.object_status.departing"),
                color: getColorClass(reservationState?.transitionIconColorAtDatePoint, "text-red-500 dark:text-red-400"),
                showInTooltip: true,
              });
            }

            if (isArriving) {
              rightIcons.push({
                icon: "log-in",
                tooltip: t("tasks.section.object_status.arriving"),
                color: getColorClass(reservationState?.transitionIconColorAtDatePoint, "text-green-500 dark:text-green-400"),
                showInTooltip: true,
              });
            }

            if (isBlocked) {
              rightIcons.push({
                icon: "lock",
                tooltip: t("tasks.section.object_status.blocked"),
                color: getColorClass(reservationState?.blockedIconColorAtDatePoint, "text-gray-800 dark:text-gray-200"),
                showInTooltip: true,
              });
            }
          }

          return {
            routeId: `${ReportListType.TYPE_PER_PROPERTY}_${property.id}`,
            text: property.name,
            icon: "house",
            to: {
              name: "report-list",
              query: {
                view: BUILTIN_REPORT_VIEW_KEYS.ALL,
                [REPORT_VIEW_SCOPE_QUERY.property]: String(property.id),
              },
            },
            children: [], // Required by VSidebarItemProps but empty
            iconColor: getColorClass(reservationState?.occupationTransitionColorAtDatePoint, "text-gray-800 dark:text-gray-100"),
            iconTooltip: tooltipText,
            rightIcons,
          };
        }),
      };
      items.push(groupItem);
    });

    return items;
  });

  const visiblePropertyTabs = reactive<Record<string, boolean>>({});

  return {
    currentListName,
    currentList,
    currentListId,
    currentListType,
    currentListTranslation,
    currentViewKey,
    currentViewDefinition,
    currentPinnedFilter,
    scope,
    isPropertyBrowseMode,
    listQueryReady,
    viewManagement,
    switcherEnabled,
    perObjectIsDefault,
    isCurrentDefault,
    effectiveViewSidebarItems,
    filteredSorts,
    loading,
    filteredTopics,
    propertyTabs,
    visiblePropertyTabs,
    currentListRouteId,
  };
}

export function provideReportList() {
  const inst = createReportList();
  provide(ReportListKey, inst);
  return inst;
}

type UseReportList = ReturnType<typeof createReportList>;
export function useReportList() {
  const inst = inject<UseReportList>(ReportListKey);

  if (!inst) {
    throw new Error("Run provideReportList before useReportList");
  }

  return inst;
}

export function tryUseReportList(): UseReportList | undefined {
  return inject<UseReportList>(ReportListKey);
}

export const isFocusedOnReportShortcut = computed(() => {
  return shortcutVoter.isFocusedOnReportShortcut();
});
